import { useState } from 'react';
import { BookingData } from '../BookingWizard';
import { bookingDetailsSchema } from '../../../lib/validations';

interface DetailsStepProps {
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
}

const DetailsStep = ({ bookingData, onUpdate, onNext }: DetailsStepProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string) => {
    const fieldSchema = bookingDetailsSchema.shape[field as keyof typeof bookingDetailsSchema.shape];
    if (!fieldSchema) return null;
    
    const result = fieldSchema.safeParse(value);
    if (!result.success) {
      return result.error.issues[0]?.message || 'Invalid input';
    }
    return null;
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const value = bookingData[field as keyof BookingData] as string;
    const error = validateField(field, value || '');
    setErrors(prev => ({ ...prev, [field]: error || '' }));
  };

  const handleChange = (field: keyof BookingData, value: string) => {
    onUpdate({ [field]: value });
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const result = bookingDetailsSchema.safeParse({
      customerName: bookingData.customerName,
      customerPhone: bookingData.customerPhone,
      customerEmail: bookingData.customerEmail,
    });
    
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      setTouched({
        customerName: true,
        customerPhone: true,
        customerEmail: true,
      });
      return;
    }
    
    onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Name *
        </label>
        <input
          type="text"
          required
          value={bookingData.customerName || ''}
          onChange={(e) => handleChange('customerName', e.target.value)}
          onBlur={() => handleBlur('customerName')}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
            errors.customerName && touched.customerName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your full name"
        />
        {errors.customerName && touched.customerName && (
          <p className="text-red-500 text-sm mt-1">{errors.customerName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number *
        </label>
        <input
          type="tel"
          required
          value={bookingData.customerPhone || ''}
          onChange={(e) => handleChange('customerPhone', e.target.value)}
          onBlur={() => handleBlur('customerPhone')}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
            errors.customerPhone && touched.customerPhone ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="+32 123 456 789"
        />
        {errors.customerPhone && touched.customerPhone && (
          <p className="text-red-500 text-sm mt-1">{errors.customerPhone}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email *
        </label>
        <input
          type="email"
          required
          value={bookingData.customerEmail || ''}
          onChange={(e) => handleChange('customerEmail', e.target.value)}
          onBlur={() => handleBlur('customerEmail')}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
            errors.customerEmail && touched.customerEmail ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="john@example.com"
        />
        {errors.customerEmail && touched.customerEmail && (
          <p className="text-red-500 text-sm mt-1">{errors.customerEmail}</p>
        )}
      </div>

      <button
        type="submit"
        className="w-full bg-primary text-white py-4 px-4 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
      >
        Continue to Summary
      </button>
    </form>
  );
};

export default DetailsStep;
